import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import StatBox from '../../components/StatBox';
import { tokens } from '../../theme';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import DownloadIcon from '@mui/icons-material/Download';

const API_BASE = `${process.env.REACT_APP_APILINK}/admin/coordinator-expenses`;

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const CoordinatorMyReports = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const now = new Date();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formDescription, setFormDescription] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [exporting, setExporting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => {
    const adminUserJson = localStorage.getItem('adminUser');
    const adminUser = adminUserJson ? JSON.parse(adminUserJson) : null;
    if (Number(adminUser?.role) !== 3) {
      navigate('/', { replace: true });
      return;
    }
  }, [navigate]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMonth) params.set('month', filterMonth);
      if (filterYear) params.set('year', filterYear);
      params.set('page', String(page));
      params.set('limit', String(pageSize));
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: { token } });
      const data = await res.json();
      if (data.success) {
        setExpenses(data.data || []);
        setTotalCount(data.pagination?.total || 0);
      }
    } catch (e) {
      setToastMessage('Failed to load expenses');
      setOpenToast(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const adminUserJson = localStorage.getItem('adminUser');
    const adminUser = adminUserJson ? JSON.parse(adminUserJson) : null;
    if (Number(adminUser?.role) !== 3) return;
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMonth, filterYear, page, pageSize]);

  const showToast = (msg) => {
    setToastMessage(msg);
    setOpenToast(true);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterMonth) params.set('month', filterMonth);
      if (filterYear) params.set('year', filterYear);
      const url = params.toString() ? `${API_BASE}/export-excel?${params.toString()}` : `${API_BASE}/export-excel`;
      const res = await fetch(url, { headers: { token } });
      if (!res.ok) {
        showToast('Export failed');
        return;
      }
      const blob = await res.blob();
      const disp = res.headers.get('Content-Disposition');
      const match = disp && disp.match(/filename="?([^";\n]+)"?/);
      const filename = match ? match[1].trim() : 'coordinator-expenses.xlsx';
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(href);
      showToast('Excel exported successfully');
    } catch (e) {
      console.error('Export Excel error', e);
      showToast('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const resetForm = () => {
    setFormDescription('');
    setFormAmount('');
    setEditingId(null);
  };

  const handleAdd = () => {
    resetForm();
    setAddOpen(true);
  };

  const handleAddSubmit = async () => {
    if (!formDescription.trim()) {
      showToast('Description is required');
      return;
    }
    const amount = Number(formAmount);
    if (Number.isNaN(amount) || amount < 0) {
      showToast('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', token },
        body: JSON.stringify({ description: formDescription.trim(), amount }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Expense added successfully');
        setAddOpen(false);
        resetForm();
        fetchExpenses();
      } else {
        showToast(data.message || 'Failed to add expense');
      }
    } catch (e) {
      showToast('Failed to add expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOpen = (row) => {
    setEditingId(row.id);
    setFormDescription(row.description);
    setFormAmount(String(row.amount));
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!formDescription.trim()) {
      showToast('Description is required');
      return;
    }
    const amount = Number(formAmount);
    if (Number.isNaN(amount) || amount < 0) {
      showToast('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', token },
        body: JSON.stringify({ description: formDescription.trim(), amount }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Expense updated successfully');
        setEditOpen(false);
        resetForm();
        fetchExpenses();
      } else {
        showToast(data.message || 'Failed to update expense');
      }
    } catch (e) {
      showToast('Failed to update expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirmId(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId == null) return;
    try {
      const res = await fetch(`${API_BASE}/${deleteConfirmId}`, { method: 'DELETE', headers: { token } });
      const data = await res.json();
      setDeleteConfirmOpen(false);
      setDeleteConfirmId(null);
      if (data.success) {
        showToast('Expense deleted');
        fetchExpenses();
      } else {
        showToast(data.message || 'Failed to delete');
      }
    } catch (e) {
      setDeleteConfirmOpen(false);
      setDeleteConfirmId(null);
      showToast('Failed to delete expense');
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString();
  };

  const totalAmount = expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
  const selectedMonthLabel =
    MONTHS.find((m) => String(m.value) === filterMonth)?.label || '';
  const filterLabel =
    filterMonth && filterYear ? `${selectedMonthLabel} ${filterYear}`.trim() : 'All';
  const subtitle = `${filterLabel} — your expenses only`;

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) {
    years.push(y);
  }

  const columns = [
    { field: 'id', headerName: 'ID', width: 70, hide: true },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 180 },
    {
      field: 'amount',
      headerName: 'Amount (€)',
      width: 110,
      valueFormatter: (params) =>
        params.value != null ? `€ ${Number(params.value).toFixed(2)}` : '',
    },
    {
      field: 'created_at',
      headerName: 'Date',
      width: 120,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'Edit',
      headerName: 'Edit',
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => (
        <Button
          sx={{
            backgroundColor: colors.greenAccent[600],
            color: colors.grey[100],
            ':hover': { backgroundColor: colors.greenAccent[700] },
          }}
          variant="contained"
          onClick={() => handleEditOpen(cellValues.row)}
          title="Edit"
        >
          <EditIcon />
        </Button>
      ),
    },
    {
      field: 'Delete',
      headerName: 'Delete',
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => (
        <Button
          sx={{
            backgroundColor: colors.redAccent[500],
            color: colors.grey[100],
            ':hover': { backgroundColor: colors.redAccent[600] },
          }}
          variant="contained"
          onClick={() => handleDeleteClick(cellValues.row.id)}
          title="Delete"
        >
          <DeleteIcon />
        </Button>
      ),
    },
  ];

  const adminUserJson = localStorage.getItem('adminUser');
  const adminUser = adminUserJson ? JSON.parse(adminUserJson) : null;
  if (Number(adminUser?.role) !== 3) {
    return null;
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap">
        <Header title="Coordinator Expenses" subtitle={subtitle} />
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            disabled={exporting}
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: colors.grey[100],
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 20px',
              '&:hover': { backgroundColor: colors.greenAccent[700] },
            }}
          >
            {exporting ? 'Exporting…' : 'Export Excel'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 20px',
              '&:hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            Add Expense
          </Button>
        </Box>
      </Box>

      {/* Summary cards - same style as dashboard / admin Coordinator View (narrow width) */}
      <Box mt={4} p={2} sx={{ background: colors.primary[500], borderRadius: 0, boxShadow: 'none', mb: 1 }}>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: 'repeat(1, minmax(0, 260px))',
            sm: 'repeat(2, minmax(0, 260px))',
            md: 'repeat(2, minmax(0, 260px))',
          }}
          gap={2}
        >
          <StatBox
            title={loading ? '...' : expenses.length}
            subtitle="Total Expenses"
            icon={<ReceiptLongIcon sx={{ color: colors.greenAccent[600], fontSize: 40 }} />}
          />
          <StatBox
            title={loading ? '...' : `€ ${Number(totalAmount).toFixed(2)}`}
            subtitle="Total Amount"
            icon={<PaymentsIcon sx={{ color: colors.greenAccent[600], fontSize: 40 }} />}
          />
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: colors.grey[100],
            fontWeight: 600,
          }}
        >
          <FilterListIcon /> FILTERS
        </Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="month-filter-label">Month</InputLabel>
          <Select
            labelId="month-filter-label"
            value={filterMonth}
            label="Month"
            onChange={(e) => setFilterMonth(e.target.value)}
            sx={{
              backgroundColor: colors.primary[400],
              color: colors.grey[100],
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[700] },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
            }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {MONTHS.map((m) => (
              <MenuItem key={m.value} value={String(m.value)}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="year-filter-label">Year</InputLabel>
          <Select
            labelId="year-filter-label"
            value={filterYear}
            label="Year"
            onChange={(e) => setFilterYear(e.target.value)}
            sx={{
              backgroundColor: colors.primary[400],
              color: colors.grey[100],
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[700] },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
            }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {years.map((y) => (
              <MenuItem key={y} value={String(y)}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid
          rows={expenses}
          columns={columns}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          disableColumnFilter
          loading={loading}
          paginationMode="server"
          page={page - 1}
          pageSize={pageSize}
          rowCount={totalCount}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(p) => { setPage(p + 1); }}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        />
      </Box>

      <Dialog open={addOpen} onClose={() => !submitting && setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Expense</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="Amount"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            value={formAmount}
            onChange={(e) => setFormAmount(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => !submitting && setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Expense</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="Amount"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            value={formAmount}
            onChange={(e) => setFormAmount(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete expense</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this expense?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openToast}
        autoHideDuration={4000}
        onClose={() => setOpenToast(false)}
        message={toastMessage}
        action={
          <IconButton size="small" color="inherit" onClick={() => setOpenToast(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

export default CoordinatorMyReports;
